import {
  MassScale,
  TemperatureScale,
  type JWTPayload,
  type Nullable,
  type TempReading,
  type Token
} from './types/RoastLog';

export const isNumber = (...args: any[]): boolean => {
  return args.every((arg) => typeof arg === 'number' && !isNaN(arg));
};

export const floatToDecimal = (f: number, precision = 1) => {
  // Keep in mind we can't just have a boolean check because f can be 0
  const floatNumber = f === undefined || f === null ? 0 : f;
  return Number(floatNumber.toFixed(precision));
};

export const sortByName = (objectArray: any[]) => {
  return objectArray.sort((a, b) => a.name.localeCompare(b.name));
};

export const sortByLabel = (objectArray: any[]) => {
  return objectArray.sort((a, b) => a.label.localeCompare(b.label));
};

export type TempConverter = (value: number) => number;

// Temperature converters
const celsiusToFahrenheit: TempConverter = (celsius: number) => celsius * 1.8 + 32;
const celsiusToFahrenheitRate: TempConverter = (celsius: number) => celsius * 1.8;
const identity: TempConverter = (value: number) => value;

export const fahrenheitToCelsius: TempConverter = (fahrenheit: number) => (fahrenheit - 32) / 1.8;

const normalizeTemperatureScale = (scale: TemperatureScale): TemperatureScale => {
  switch (scale) {
    case TemperatureScale.CelsiusLower:
      return TemperatureScale.Celsius;
    case TemperatureScale.FahrenheitLower:
      return TemperatureScale.Fahrenheit;
    default:
      return scale;
  }
};

/**
 * Convert a temperature to celsius. This is used when the a temperature must be in celsius, as in
 * the temperature offsets.
 */
export const getCelsiusIncrementalConverter = (tempScale: TemperatureScale): TempConverter => {
  const scale = normalizeTemperatureScale(tempScale);
  if (scale === TemperatureScale.Celsius) {
    return identity;
  } else {
    return (t) => t / 1.8;
  }
};

export const getIncrementalTempConverter = (tempScale: TemperatureScale): TempConverter => {
  const scale = normalizeTemperatureScale(tempScale);
  if (scale === TemperatureScale.Celsius) {
    return identity;
  } else {
    return (f) => f * 1.8;
  }
};

export const getTemperatureConverter = (tempScale: TemperatureScale): TempConverter => {
  const scale = normalizeTemperatureScale(tempScale);
  if (scale === TemperatureScale.Celsius) {
    return identity;
  } else {
    return celsiusToFahrenheit;
  }
};

export const getHeatingRateConverter = (tempScale: TemperatureScale): TempConverter => {
  const scale = normalizeTemperatureScale(tempScale);
  if (scale === TemperatureScale.Celsius) {
    return identity;
  } else {
    return celsiusToFahrenheitRate;
  }
};

export const formatTemperature = (value: TempReading): Nullable<number> => {
  if (!value) {
    return null;
  }

  return parseFloat(value.toFixed(1));
};

// mass converters
const lbsToGrams = (n: number): number => floatToDecimal(n * 453.59237, 1);
const kgToGrams = (n: number): number => floatToDecimal(n * 1000, 1);

export const massToGrams = (massScale: MassScale, n: number | undefined | null): number => {
  if (n === undefined || n === null) {
    return 0;
  }

  switch (massScale) {
    case MassScale.Pounds:
      return lbsToGrams(n);
    case MassScale.Kilograms:
      return kgToGrams(n);
    case MassScale.Grams:
      return floatToDecimal(n, 1); // Already in grams, no conversion needed.
    default:
      return 0; // Return 0 for any unexpected scale.
  }
};

// Time converters
export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  // Pad minutes and seconds with leading zeros if needed
  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(remainingSeconds).padStart(2, '0');

  return `${formattedMinutes}:${formattedSeconds}`;
};

export const displayTimeToSeconds = (timeStr: string): number => {
  const [minutes, seconds] = timeStr.split(':').map(Number);

  if (!isNumber(minutes) || !isNumber(seconds) || seconds >= 60) {
    throw new Error('Invalid time format. Please use MM:SS format');
  }

  return minutes * 60 + seconds;
};
